// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE capability manifest — runtime derivation (drift-proof)
// ═══════════════════════════════════════════════════════════════════════════
//
// Builds the capability manifest at RUNTIME from the SAME objects that enforce
// the safe contour — there is NO hand-maintained command list (a hand list
// drifts; the anti-drift vitest fails the moment it would):
//
//   • the command set        — `aiCommandSchema` union variants ∩
//                              `isAiCallableCommand` (only AI-callable targets);
//   • action → command        — the 5 `*_AI_ACTION_TARGET_COMMAND` maps
//                              (action is the discriminant the agent sends);
//   • per-action inputSchema   — `z.toJSONSchema(variant)` of THAT action's
//                              union variant (NOT the empty `aiSchemaRef`, NOT
//                              the lossy descriptor `parameters[]`);
//   • description / domain /    — projected verbatim from the registry
//     aliases / examples         descriptor the action targets.
//
// Shape follows MCP `tools/list` + Anthropic tool-definition
// (`name`/`description`/`inputSchema` per command), so a Claude agent reads it
// natively.
// ═══════════════════════════════════════════════════════════════════════════

import { z } from "zod";

import {
  ALL_COMMAND_DESCRIPTORS,
  getDescriptorById,
  type CommandDescriptor,
} from "@dxf-ai-editor/command-registry";

import { isAiCallableCommand } from "../aiCallableGuard.js";
import { AI_ACTION_TARGET_COMMAND } from "../aiCallableGuard.js";
import { AI_ENTITY_ACTION_TARGET_COMMAND } from "../aiCallableGuard.js";
import { DRAW_MODIFY_AI_ACTION_TARGET_COMMAND } from "../drawModifyGuard.js";
import { VIEW_SPACE_AI_ACTION_TARGET_COMMAND } from "../viewSpaceGuard.js";
import { MATCHPROP_AI_ACTION_TARGET_COMMAND } from "../matchpropGuard.js";
import { BASE_PRIM_AI_ACTION_TARGET_COMMAND } from "../basePrimGuard.js";
import { LAYER_AI_ACTION_TARGET_COMMAND } from "../layerGuard.js";
// Import the combined union from the SCHEMA-ONLY module — NOT from
// `aiDispatcher.js`, which imports every domain dispatcher/translator at module
// scope. Building the manifest is a PURE DESCRIPTION of the surface (INV-1), so
// it must stay off the dispatch path: `aiCommandSchema.js` depends only on the
// domain schema modules (`zod` + `./blockSchemas`), keeping `GET /api/manifest`
// decoupled from `applyCommand` and the translator graph.
import { aiCommandSchema } from "../aiCommandSchema.js";

import {
  AGENT_BRIDGE_PROTOCOL_PRIMER,
  AGENT_BRIDGE_REJECT_TAXONOMY,
} from "./rejectTaxonomy.js";
import type {
  CapabilityManifest,
  JsonSchemaObject,
  ManifestCommand,
  ManifestLimits,
} from "./manifestTypes.js";

/** Bridge protocol version (the wire-contract revision the manifest describes). */
export const AGENT_BRIDGE_PROTOCOL_VERSION = "1.0.0";

/**
 * The STATIC (advisory) request-sizing caps the connection spec's
 * `GET /api/manifest` contract requires (§3.4 [N5] / §3.1) so a sessionless
 * agent can pre-size a batch before a browser is paired — instead of discovering
 * the caps only by tripping `413`/`429`. These are NON-AUTHORITATIVE in the
 * static snapshot (`authoritative` is stamped per-source below); the running
 * companion's actual `413`/`429` are authoritative. Values mirror the spec
 * envelope: `stateLimitMax` = the §3.2 `limit` max (1000); `screenshotMaxPx` =
 * the ПРИОРИТЕТ #3 hard 2000px ceiling; `maxBatchBytes` = the ~1 MB mutating-POST
 * body cap ([L9]); `maxBatch` = a coarse element cap ([§3.1 batch]).
 */
export const AGENT_BRIDGE_STATIC_LIMITS: Omit<ManifestLimits, "authoritative"> = {
  maxBatch: 512,
  maxBatchBytes: 1_048_576,
  stateLimitMax: 1000,
  screenshotMaxPx: 2000,
};

/** Stamps the manifest derivation generator (provenance, version-locked). */
export const AGENT_BRIDGE_MANIFEST_GENERATOR_VERSION = "agent-bridge-manifest@1.0.0";

/**
 * The registry descriptor-schema revision the manifest projects (mirrors the
 * generated `command-registry.json` `descriptorSchemaVersion`: 2 = the
 * aiCallable-bearing shape, CC-2a). Bumped in lock-step if the registry shape
 * the manifest reads (aliases / help / aiCallable) ever changes.
 */
export const AGENT_BRIDGE_DESCRIPTOR_SCHEMA_VERSION = 2;

/**
 * The single union-of-all-maps used to resolve an `action` → target command id.
 * Spreading the `*_AI_ACTION_TARGET_COMMAND` maps means the resolver stays in
 * lock-step with the guards automatically (no re-listed action set). The maps
 * are disjoint by action namespace, so a plain merge is total + unambiguous.
 */
const ACTION_TARGET_COMMAND: Readonly<Record<string, string>> = {
  ...AI_ACTION_TARGET_COMMAND,
  ...AI_ENTITY_ACTION_TARGET_COMMAND,
  ...DRAW_MODIFY_AI_ACTION_TARGET_COMMAND,
  ...VIEW_SPACE_AI_ACTION_TARGET_COMMAND,
  ...MATCHPROP_AI_ACTION_TARGET_COMMAND,
  ...BASE_PRIM_AI_ACTION_TARGET_COMMAND,
  ...LAYER_AI_ACTION_TARGET_COMMAND,
};

/**
 * The merged action→command map, exported read-only so the anti-drift test can
 * enumerate the AUTHORITATIVE action universe (every map entry) without
 * re-listing it.
 */
export const AGENT_BRIDGE_ACTION_TARGET_COMMAND = ACTION_TARGET_COMMAND;

/**
 * The AUTHORITATIVE aiCallable-action universe: every action in the merged
 * `*_AI_ACTION_TARGET_COMMAND` maps whose target command is `aiCallable: true`,
 * sorted. This is the exact set the manifest MUST advertise (anti-drift basis)
 * — exported so consumers (the endpoint test, the future browser live path) can
 * assert parity WITHOUT reaching into `command-registry` directly.
 */
export function aiCallableActionUniverse(
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): string[] {
  return Object.entries(ACTION_TARGET_COMMAND)
    .filter(([, commandId]) => isAiCallableCommand(commandId, descriptors))
    .map(([action]) => action)
    .sort();
}

/** A union variant's runtime shape (zod-4 internals), narrowed for the discriminator read. */
type DiscriminatedVariant = {
  readonly shape?: Record<string, unknown>;
  readonly _zod?: { readonly def?: { readonly shape?: Record<string, unknown> } };
};

/**
 * Read the `action` discriminant literal off one `aiCommandSchema` union
 * variant. zod-4 exposes the object shape via `.shape`; the discriminant is a
 * `z.literal(...)` whose single value lives on its `def.values[0]`. Returns
 * `null` when the variant has no string-literal `action` (never expected for
 * the discriminated union, but kept fail-closed).
 */
export function readVariantAction(variant: unknown): string | null {
  const v = variant as DiscriminatedVariant;
  const shape = v.shape ?? v._zod?.def?.shape;
  const actionField = shape?.action as
    | {
        readonly def?: { readonly values?: readonly unknown[] };
        readonly _zod?: { readonly def?: { readonly values?: readonly unknown[] } };
      }
    | undefined;
  const values =
    actionField?.def?.values ?? actionField?._zod?.def?.values ?? undefined;
  const first = values?.[0];
  return typeof first === "string" ? first : null;
}

/**
 * The IDENTICAL `z.toJSONSchema` options used at every call site (the builder
 * AND the anti-drift round-trip test) so the emitted schema is byte-stable and
 * single-sourced. CRITICAL: `io: "input"` — the manifest `inputSchema` is the
 * shape the AGENT SENDS (the parser's INPUT), not the parsed OUTPUT. zod-4's
 * `z.toJSONSchema` defaults to the OUTPUT type, which marks every `.default(...)`
 * field as `required` (e.g. `CREATE_LINE.start.z`, `CREATE_TEXT.rotation`,
 * hatch/revcloud pattern + style defaults). `parseAiCommand` accepts those
 * fields OMITTED (it materialises the default), so an agent that validates /
 * generates against the OUTPUT schema would reject or over-specify a valid
 * minimal AiCommand. `io: "input"` emits the agent-facing input contract where
 * defaulted fields are optional.
 */
export const MANIFEST_JSON_SCHEMA_OPTIONS = {
  target: "draft-2020-12",
  io: "input",
} as const satisfies Parameters<typeof z.toJSONSchema>[1];

/**
 * Convert one union variant to a draft-2020-12 JSON Schema (input mode). Thin
 * wrapper around the native zod-4 `z.toJSONSchema` (no extra dependency) so the
 * call site + the anti-drift round-trip test use the IDENTICAL options.
 */
export function variantToJsonSchema(variant: unknown): JsonSchemaObject {
  // `z.toJSONSchema` accepts any Zod schema; the union variant is a ZodObject.
  return z.toJSONSchema(
    variant as Parameters<typeof z.toJSONSchema>[0],
    MANIFEST_JSON_SCHEMA_OPTIONS,
  ) as JsonSchemaObject;
}

/**
 * FLOAT_EPSILON — the degeneracy floor mirrored from the downstream contract
 * (packages/contracts/src/editor.ts) AND the AI schemas (drawModifySchemas.ts).
 * Kept local (no cross-package import) so the manifest stays off the dispatch /
 * contract graph (INV-1); the anti-drift test pins it to the schema's threshold.
 * Declared BEFORE {@link MANIFEST_SCHEMA_CONSTRAINTS} (which references it in the
 * OFFSET/SCALE scalarBounds) to avoid a module-load TDZ ReferenceError.
 */
const FLOAT_EPSILON = 1e-12;

/**
 * LAYER_ZERO_TRIMMED_PATTERN — round-25 P3 (github PR #527 review). Matches
 * exactly the strings `createLayerAiSchema`/`renameLayerAiSchema`
 * (layerSchemas.ts) reject as "layer 0": a `.trim()` runs BEFORE the
 * `!== "0"` refine, so any string that trims to `"0"` (bare `"0"`, or the
 * SAME value padded with leading/trailing whitespace, e.g. `" 0 "`) is
 * rejected. `\s` mirrors JS `.trim()`'s whitespace set closely enough for
 * this narrow single-character-payload case. Anchored (`^...$`) so it never
 * matches a real layer name that merely CONTAINS "0" (e.g. "L0"). Declared
 * BEFORE {@link MANIFEST_SCHEMA_CONSTRAINTS} (referenced in
 * RENAME_LAYER/CREATE_LAYER's propertyConstraints) to avoid a module-load
 * TDZ ReferenceError.
 */
const LAYER_ZERO_TRIMMED_PATTERN = "^\\s*0\\s*$";

/**
 * NON_WHITESPACE_PATTERN — round-26 P3 (github PR #527 review). Every layer
 * NAME field (`CREATE_LAYER.name`, `RENAME_LAYER.oldName`/`newName`,
 * `SET_ACTIVE_LAYER.name`) is `z.string().trim().min(1)` in layerSchemas.ts —
 * the runtime schema TRIMS before enforcing `.min(1)`, so a whitespace-only
 * value (e.g. `"   "`) is rejected (trims to `""`, fails `min(1)`). JSON
 * Schema's plain `minLength: 1` (what `z.toJSONSchema` emits for `.min(1)`)
 * counts RAW characters — it CANNOT see the trim step, so it wrongly accepts
 * `"   "`. An agent validating ONLY against `/manifest` would accept a
 * whitespace-only name and then get a 422 at submit time (the manifest
 * under-restricts). JSON Schema's `pattern` is an UNANCHORED search (per the
 * JSON Schema spec — not assumed anchored), so `\S` here means "contains at
 * least one non-whitespace character somewhere", which is the exact
 * "non-empty after trim" check the runtime schema performs. Declared
 * alongside {@link LAYER_ZERO_TRIMMED_PATTERN} for the same reason (avoids a
 * module-load TDZ ReferenceError from {@link MANIFEST_SCHEMA_CONSTRAINTS}).
 */
const NON_WHITESPACE_PATTERN = "\\S";

/**
 * Per-action JSON-Schema override for refines that `z.toJSONSchema` SILENTLY
 * DROPS but that JSON Schema CAN express — cross-field shape preconditions
 * (`anyOf` of allowed shapes) and/or scalar-field bounds (`exclusiveMinimum`).
 *
 * A Zod `.refine(...)` is a runtime predicate `z.toJSONSchema` drops — so the
 * emitted base schema would tell an agent that `{ action: "CREATE_HATCH" }` or
 * `{ action: "OFFSET", distance: 1e-20 }` is valid, only for the safe contour to
 * reject it as a `422` at submit time (the manifest would LIE to the agent).
 * Each entry projects the dropped predicate back into the published schema so the
 * agent can pre-validate LOCALLY against `/api/manifest` before submitting:
 *   • `anyOf` — the JSON-Schema-expressible alternatives (mirrors a cross-field
 *      `.refine`: each branch only adds `required` / narrows a property to a
 *      `const`; the base object's `properties` / `type` apply to every branch);
 *   • `scalarBounds` — per-field numeric bound patches (mirrors a scalar
 *      `.refine` like `distance > FLOAT_EPSILON` → `exclusiveMinimum: 1e-12`);
 *      merged onto that field's emitted schema.
 * The branches/bounds mirror the `.refine` predicate EXACTLY (one source of
 * truth — the refine stays the authoritative runtime guard; this is its
 * JSON-Schema projection for the manifest).
 *
 * Keyed by the AI `action`. A predicate JSON Schema CANNOT express (a two-point
 * distance, a `Number.isFinite` guard) is surfaced via
 * {@link MANIFEST_PRECONDITION_NOTES} instead — NEVER fabricated as a false
 * `anyOf`. {@link REFINED_ACTIONS} lists every refined action; the anti-drift
 * test fails if a refined action is covered by NEITHER table.
 */
export const MANIFEST_SCHEMA_CONSTRAINTS: Readonly<
  Record<
    string,
    {
      readonly anyOf?: readonly JsonSchemaObject[];
      readonly scalarBounds?: Readonly<Record<string, JsonSchemaObject>>;
      /**
       * Per-property schema patches that apply to a named OPTIONAL property
       * WHENEVER it is present — regardless of any sibling discriminant. Mirrors a
       * NESTED `.refine` on an optional sub-object (e.g. `arrayEditPathSchema.refine`
       * on the optional `path` field, which Zod runs unconditionally whenever `path`
       * is supplied, not only inside the `mode:"path"` branch). Deep-merged onto the
       * named field's emitted schema, so JSON Schema validates it exactly when the
       * property exists — closing the cross-mode acceptance gap a `mode`-scoped
       * `anyOf` branch would leave open.
       */
      readonly propertyConstraints?: Readonly<Record<string, JsonSchemaObject>>;
    }
  >
> = {
  // CREATE_HATCH: exactly one boundary authoring mode must be complete —
  // rectangular corner-pick (BOTH corners) OR a non-empty internal-pick
  // boundaryPaths. Mirrors `createHatchAiSchema.refine` (entitySchemas.ts).
  CREATE_HATCH: {
    anyOf: [
      { required: ["boundaryCorner1", "boundaryCorner2"] },
      { required: ["boundaryPaths"], properties: { boundaryPaths: { minItems: 1 } } },
    ],
  },
  // CREATE_REVCLOUD: the payload required by `creationMode`. `creationMode`
  // DEFAULTS to "Rectangular", so an OMITTED creationMode must behave EXACTLY
  // like an explicit "Rectangular" (it needs corner1+corner2). JSON Schema only
  // applies a branch's `properties` when the key is PRESENT, so:
  //   • the Rectangular branch does NOT require `creationMode` — it matches BOTH
  //     an explicit "Rectangular" AND an omitted creationMode (the default),
  //     while still requiring corner1+corner2. An omitted-mode payload with only
  //     `vertices` (no corners) fails this branch (no corners) AND the other
  //     branches (they DO require `creationMode`), so it is rejected — matching
  //     the safe contour, whose Zod default routes it to Rectangular and the
  //     refine 422s it for missing corners (the original r3 P2 bug).
  //   • the Polygonal / Freehand / Object branches DO `required: ["creationMode"]`
  //     — an omitted creationMode defaults to Rectangular (not these), so they
  //     must only match when the mode is EXPLICITLY present.
  // Mirrors `createRevcloudAiSchema.refine` (entitySchemas.ts) + its `.default`.
  CREATE_REVCLOUD: {
    anyOf: [
      {
        properties: { creationMode: { const: "Rectangular" } },
        required: ["corner1", "corner2"],
      },
      {
        properties: { creationMode: { const: "Polygonal" } },
        required: ["creationMode", "vertices"],
      },
      {
        properties: { creationMode: { const: "Freehand" } },
        required: ["creationMode", "vertices"],
      },
      {
        properties: { creationMode: { const: "Object" } },
        required: ["creationMode", "sourceEntityId"],
      },
    ],
  },
  // CREATE_DIMENSION: a 3-point angular dimension is defined by its VERTEX
  // (`thirdPoint`); without it the reducer falls back to dimensionLinePoint and
  // produces a WRONG angle. Mirrors `createDimensionAiSchema.refine`
  // (entitySchemas.ts): `dimensionKind !== "angular3point" || thirdPoint`. Two
  // branches: angular3point (thirdPoint required) XOR every other kind. The
  // non-angular3point branch enumerates the other six literals (JSON Schema has
  // no native "not const", and `dimensionKind` defaults to "linear" so it may be
  // omitted — covered by the absence of a `creationMode`-style requirement).
  CREATE_DIMENSION: {
    anyOf: [
      {
        properties: { dimensionKind: { const: "angular3point" } },
        required: ["dimensionKind", "thirdPoint"],
      },
      {
        properties: {
          dimensionKind: {
            enum: [
              "linear",
              "aligned",
              "angular",
              "diameter",
              "radius",
              "ordinate",
            ],
          },
        },
      },
    ],
  },
  // ARRAYEDIT: the param block matching `mode` MUST be present, AND the nested
  // path block's divide/measure rule (divide needs itemCount>=2, measure needs
  // itemSpacing). Mirrors BOTH refines in drawModifySchemas.ts:
  //   • the outer `arrayEditAiSchema.refine` (mode→block presence) → the `anyOf`
  //     of mode branches below;
  //   • the nested `arrayEditPathSchema.refine` (divide→itemCount>=2 /
  //     measure→itemSpacing) → the `propertyConstraints.path` below.
  // The nested refine lives on the OPTIONAL `path` field, so Zod runs it WHENEVER
  // `path` is supplied — even for mode:rect/polar (a valid rect edit carrying a
  // stray incomplete `path` is 422-rejected by the contour). A `mode:"path"`-scoped
  // anyOf branch would NOT catch that cross-mode case (it accepts rect+stray-path),
  // so the divide/measure rule is projected as a PROPERTY-level constraint on
  // `path` (validated exactly when `path` exists, regardless of `mode`) — mirroring
  // the unconditional optional-field refine and giving Ajv↔Zod parity for the
  // cross-mode payload.
  ARRAYEDIT: {
    anyOf: [
      { properties: { mode: { const: "rect" } }, required: ["mode", "rect"] },
      { properties: { mode: { const: "polar" } }, required: ["mode", "polar"] },
      { properties: { mode: { const: "path" } }, required: ["mode", "path"] },
    ],
    propertyConstraints: {
      // Whenever `path` is present (ANY mode), it must satisfy the divide/measure
      // completeness rule — mirrors `arrayEditPathSchema.refine` exactly.
      path: {
        anyOf: [
          {
            properties: {
              method: { const: "divide" },
              itemCount: { minimum: 2 },
            },
            required: ["method", "itemCount"],
          },
          {
            properties: { method: { const: "measure" } },
            required: ["method", "itemSpacing"],
          },
        ],
      },
    },
  },
  // OFFSET: `distance` must be strictly greater than FLOAT_EPSILON (1e-12) — the
  // scalar `offsetAiSchema.distance.refine((d) => d > FLOAT_EPSILON)` that
  // z.toJSONSchema drops (emitting a bare `{type:"number"}`). `exclusiveMinimum`
  // mirrors `> FLOAT_EPSILON` EXACTLY (a value strictly above the floor).
  OFFSET: { scalarBounds: { distance: { exclusiveMinimum: FLOAT_EPSILON } } },
  // SCALE: `factor` must be a finite number strictly greater than FLOAT_EPSILON
  // — `scaleAiSchema.factor.refine((f) => Number.isFinite(f) && f > FLOAT_EPSILON)`.
  // `exclusiveMinimum` mirrors `> FLOAT_EPSILON` exactly. The `Number.isFinite`
  // arm is NOT expressible per-field in JSON Schema (`exclusiveMinimum:1e-12`
  // still admits +Infinity, since Infinity > 1e-12) — but finiteness is a
  // UNIVERSAL safe-contour property (zod's `z.number()` rejects NaN/±Infinity for
  // EVERY number field, not just `factor`), so it is surfaced ONCE in the
  // protocol primer's `numericFiniteness` rule rather than fabricated as a
  // per-action note here. (See AGENT_BRIDGE_PROTOCOL_PRIMER.numericFiniteness.)
  SCALE: { scalarBounds: { factor: { exclusiveMinimum: FLOAT_EPSILON } } },
  // RENAME_LAYER (T7, agent-bridge layer-mgmt) — `oldName` must not be "0"
  // (layer "0" can never be renamed, AutoCAD parity — mirrors "Standard" for
  // DIMSTYLE). Mirrors `renameLayerAiSchema.refine` (layerSchemas.ts) exactly.
  // `not: { pattern } }` is a single-field, unconditional constraint (not a
  // cross-branch `anyOf`), so it rides on `propertyConstraints` like
  // ARRAYEDIT.path — merged onto `oldName`'s own emitted schema regardless of
  // any sibling field. Round-21 P2 (github PR #527 review) — `newName` must
  // ALSO not be "0" (layer "0" is mandatory/always-occupied, so renaming
  // ANOTHER layer to "0" always collides) — mirrors the schema's SECOND
  // `.refine` exactly.
  //
  // Round-25 P3 (github PR #527 review) — `not: { const: "0" } }` only
  // rejected the bare literal `"0"`, but the schema's `.trim()` runs BEFORE
  // the `!== "0"` refine (`z.string().trim().min(1)`), so a whitespace-
  // padded `" 0 "` is ALSO rejected at runtime (trims to `"0"` first). The
  // manifest's plain `const` check couldn't see that — an agent validating
  // ONLY against `/manifest` would accept `" 0 "` and then get a 422 at
  // submit time (the manifest lied by under-restricting). `not: { pattern:
  // ... } }` (a whitespace* + "0" + whitespace* anchor) mirrors the
  // trim-then-compare runtime check exactly — JSON Schema CAN express this
  // one (unlike a genuine cross-field/numeric predicate), so it rides on
  // `propertyConstraints` rather than a precondition note.
  //
  // Round-26 P3 (github PR #527 review) — ALSO reject a raw `minLength:1`
  // pass that is whitespace-only (e.g. `"   "`): `z.toJSONSchema` emits
  // `minLength:1` for `.min(1)`, which counts RAW characters and cannot see
  // the `.trim()` that runs first at runtime. `pattern: NON_WHITESPACE_
  // PATTERN` (sibling key, ANDed with the `not` above) closes that gap for
  // BOTH `oldName` and `newName`.
  RENAME_LAYER: {
    propertyConstraints: {
      oldName: {
        not: { pattern: LAYER_ZERO_TRIMMED_PATTERN },
        pattern: NON_WHITESPACE_PATTERN,
      },
      newName: {
        not: { pattern: LAYER_ZERO_TRIMMED_PATTERN },
        pattern: NON_WHITESPACE_PATTERN,
      },
    },
  },
  // CREATE_LAYER (T7, agent-bridge layer-mgmt) — round-25 P2 (github PR #527
  // review) — `name` must not be "0" (any whitespace-padding included, same
  // trim-then-compare reasoning as RENAME_LAYER above), mirroring
  // `createLayerAiSchema.refine` (layerSchemas.ts) exactly: layer "0" already
  // exists (mandatory, AutoCAD parity), so CREATE_LAYER{name:"0"} always
  // collides. Round-26 P3 — ALSO the same whitespace-only gap as
  // RENAME_LAYER above.
  CREATE_LAYER: {
    propertyConstraints: {
      name: {
        not: { pattern: LAYER_ZERO_TRIMMED_PATTERN },
        pattern: NON_WHITESPACE_PATTERN,
      },
    },
  },
  // SET_ACTIVE_LAYER (T7, agent-bridge layer-mgmt) — round-26 P3 (github PR
  // #527 review) — `name` is `z.string().trim().min(1)` with NO `.refine`
  // (unlike CREATE_LAYER/RENAME_LAYER, `name:"0"` IS a valid target — moving
  // CLAYER to layer "0" is legitimate), so this action carries ONLY the
  // whitespace-only gap fix, no "0" rejection. Not in `REFINED_ACTIONS`
  // (that set audits OBJECT-level `.refine`/`.superRefine`, which this
  // schema has none of — a plain `.trim().min(1)` field chain is not an
  // object-level refine) — `MANIFEST_SCHEMA_CONSTRAINTS` is a separate,
  // unconditional mechanism that any action may use.
  SET_ACTIVE_LAYER: {
    propertyConstraints: {
      name: { pattern: NON_WHITESPACE_PATTERN },
    },
  },
};

/**
 * Runtime PRECONDITION notes for refines JSON Schema CANNOT express, surfaced
 * verbatim on the manifest command so the agent obeys them without tripping a
 * `422`. Each note mirrors a `.refine` predicate that is NOT a pure
 * single-field / shape constraint (a two-point Euclidean distance, a
 * `Number.isFinite` guard) — projected as human language, NEVER fabricated as a
 * false `anyOf`. Keyed by AI `action`.
 */
export const MANIFEST_PRECONDITION_NOTES: Readonly<
  Record<string, readonly string[]>
> = {
  // MIRROR: the mirror axis must be NONDEGENERATE — the Euclidean distance
  // between mirrorLine1 and mirrorLine2 must exceed FLOAT_EPSILON (1e-12). This
  // is a two-point cross-field distance predicate JSON Schema cannot encode.
  // Mirrors `mirrorAiSchema.refine` (drawModifySchemas.ts).
  MIRROR: [
    "The mirror axis must be nondegenerate: the Euclidean distance between mirrorLine1 and mirrorLine2 must be greater than 1e-12 (the two points must differ). JSON Schema cannot encode a two-point distance — the safe contour rejects a coincident axis as 422.",
  ],
  // ROTATE: `angleDeg` must be FINITE — its `.refine` is EXACTLY a finiteness
  // guard (the only predicate on the field), so it gets an explicit per-action
  // note in addition to the universal `numericFiniteness` primer rule. JSON's
  // `{type:"number"}` cannot encode finiteness (+Infinity passes the schema),
  // so the contour's reject is documented here. Mirrors `rotateAiSchema`
  // (basePrimSchemas.ts): `.refine((a) => !Number.isNaN(a) && Number.isFinite(a))`.
  ROTATE: [
    "angleDeg must be a finite number (not NaN or ±Infinity); the safe contour rejects a non-finite rotation angle as 422 (see also the manifest's numericFiniteness rule, which applies to every numeric field).",
  ],
  // DELETE_ENTITIES (ERASE): SCOPE note (not a refine — DELETE_ENTITIES carries no
  // `.refine`, so it is NOT in REFINED_ACTIONS; this note documents the honest
  // contract boundary of the underlying `shell/delete-entities` command). ERASE
  // deletes only SPACE entities (model/paper/layout entities). Block-definition
  // children — most notably an ATTDEF surfaced by CREATE_ATTDEF as an `attdef-*`
  // id — live in a block DEFINITION, NOT a space, and are NOT deletable through
  // DELETE_ENTITIES: such an id is silently skipped by the reducer (no-op, no
  // throw). The turn's `removedEntityIds` (the create→read→erase loop's "what was
  // actually removed" signal) therefore EXCLUDES a non-space id, so the agent can
  // detect the no-op instead of assuming the delete succeeded.
  DELETE_ENTITIES: [
    "DELETE_ENTITIES (ERASE) removes only SPACE entities (model/paper/layout). A block-definition or ATTDEF id (e.g. an attdef-* id returned by CREATE_ATTDEF) is NOT deletable via DELETE_ENTITIES — it is skipped as a no-op (no error). Confirm the delete via the turn result's removedEntityIds, which lists exactly the ids that were actually removed (a non-space id will be absent).",
  ],
};

/**
 * The AUTHORITATIVE set of actions whose Zod variant carries a `.refine` /
 * `.superRefine` (object-level, nested, or scalar-field). DERIVED by auditing
 * every schema module (entitySchemas, drawModifySchemas, basePrimSchemas,
 * blockSchemas, viewSpaceSchemas) for `.refine` / `.superRefine` — kept explicit
 * because zod-4 introspection only reliably surfaces OBJECT-level refines
 * (`_zod.def.checks`), NOT scalar-field refines (OFFSET.distance, SCALE.factor,
 * ROTATE.angleDeg) nor nested refines (ARRAYEDIT.path). The anti-drift test
 * (a) asserts every object-level refine introspected off the union IS in this
 * set (so a NEW object refine can't silently escape the list), and (b) asserts
 * every action in this set is covered by a JSON-Schema override
 * ({@link MANIFEST_SCHEMA_CONSTRAINTS}) OR a precondition note
 * ({@link MANIFEST_PRECONDITION_NOTES}) — so a refine can never ship un-projected.
 */
export const REFINED_ACTIONS: ReadonlySet<string> = new Set([
  // Object-level cross-field refines (entitySchemas.ts):
  "CREATE_HATCH",
  "CREATE_REVCLOUD",
  "CREATE_DIMENSION",
  // Object-level cross-field refines (drawModifySchemas.ts):
  "MIRROR",
  "ARRAYEDIT", // outer mode→block refine + nested arrayEditPathSchema.refine
  // Scalar-field refines (drawModifySchemas.ts):
  "OFFSET", // distance > FLOAT_EPSILON
  "SCALE", // factor finite && > FLOAT_EPSILON
  // Scalar-field refine (basePrimSchemas.ts):
  "ROTATE", // angleDeg finite
  // Object-level single-field refine (layerSchemas.ts, T7 agent-bridge):
  "RENAME_LAYER", // oldName !== "0" (layer 0 cannot be renamed)
  "CREATE_LAYER", // name !== "0" (layer 0 already exists — round-25)
]);

/**
 * Deep-merge a per-field scalar-bound patch onto an emitted JSON Schema's
 * `properties`. Only touches the named fields (merging the bound keys onto each
 * field's schema); leaves the rest of the schema byte-stable. Returns a new
 * object (no mutation of the input).
 */
function applyScalarBounds(
  base: JsonSchemaObject,
  scalarBounds: Readonly<Record<string, JsonSchemaObject>>,
): JsonSchemaObject {
  const baseProps = (base.properties as Record<string, JsonSchemaObject>) ?? {};
  const mergedProps: Record<string, JsonSchemaObject> = { ...baseProps };
  for (const [field, bound] of Object.entries(scalarBounds)) {
    mergedProps[field] = { ...(baseProps[field] ?? {}), ...bound };
  }
  return { ...base, properties: mergedProps };
}

/**
 * Deep-merge a per-property schema patch onto an emitted JSON Schema's
 * `properties` — like {@link applyScalarBounds} but the patch is an arbitrary
 * sub-schema (e.g. a nested `anyOf`), not just scalar bound keys. Used to project
 * a NESTED refine on an OPTIONAL sub-object (e.g. `arrayEditPathSchema.refine` on
 * the optional `path` field): the patch rides on the named property's schema, so
 * JSON Schema validates it WHENEVER the property is present, regardless of any
 * sibling discriminant — mirroring Zod's unconditional optional-field validation.
 * Returns a new object (no mutation of the input).
 */
function applyPropertyConstraints(
  base: JsonSchemaObject,
  propertyConstraints: Readonly<Record<string, JsonSchemaObject>>,
): JsonSchemaObject {
  const baseProps = (base.properties as Record<string, JsonSchemaObject>) ?? {};
  const mergedProps: Record<string, JsonSchemaObject> = { ...baseProps };
  for (const [field, patch] of Object.entries(propertyConstraints)) {
    mergedProps[field] = { ...(baseProps[field] ?? {}), ...patch };
  }
  return { ...base, properties: mergedProps };
}

/**
 * Emit the agent-facing `inputSchema` for one action's union variant: the
 * native `z.toJSONSchema(...)` (input mode) PLUS any override that
 * `z.toJSONSchema` silently dropped — a cross-field `anyOf` and/or scalar-field
 * bound patch (see {@link MANIFEST_SCHEMA_CONSTRAINTS}). Single derivation path
 * shared by the builder and the anti-drift round-trip test, so the published
 * schema is byte-stable + provably the refine's JSON-Schema projection.
 * Predicates JSON Schema cannot express ride on the command's `preconditions`
 * (see {@link MANIFEST_PRECONDITION_NOTES}), NOT here.
 */
export function buildActionInputSchema(
  action: string,
  variant: unknown,
): JsonSchemaObject {
  const base = variantToJsonSchema(variant);
  const constraint = MANIFEST_SCHEMA_CONSTRAINTS[action];
  if (constraint === undefined) return base;
  let schema = base;
  if (constraint.scalarBounds !== undefined) {
    schema = applyScalarBounds(schema, constraint.scalarBounds);
  }
  if (constraint.propertyConstraints !== undefined) {
    // Project a nested refine onto an OPTIONAL property so it validates whenever
    // that property is present (regardless of a sibling `mode` discriminant) —
    // closing the cross-mode acceptance gap a `mode`-scoped `anyOf` branch leaves.
    schema = applyPropertyConstraints(schema, constraint.propertyConstraints);
  }
  if (constraint.anyOf !== undefined) {
    // Wrap the cross-field precondition as `anyOf` of the allowed shapes. Each
    // branch only adds `required` / narrows a property to a `const` (or nests
    // its own `anyOf`); the base object's `properties` / `type` apply to all.
    schema = { ...schema, anyOf: constraint.anyOf.map((branch) => ({ ...branch })) };
  }
  return schema;
}

/**
 * Look up the precondition notes for an action (refines JSON Schema cannot
 * express). Returns `undefined` when the action has none, so the field is
 * omitted from the manifest command (kept absent rather than empty-array for a
 * lean wire shape).
 */
export function actionPreconditions(action: string): readonly string[] | undefined {
  return MANIFEST_PRECONDITION_NOTES[action];
}

/**
 * Map one AI-callable union variant to a {@link ManifestCommand}. Returns `null`
 * when the variant is not AI-callable (its target command's `aiCallable` flag is
 * false / the target is unknown) — those variants are excluded from the manifest
 * (only the AI-callable surface is advertised). Also `null` for a variant whose
 * action is unreadable or unmapped (fail-closed — never emit a half-formed entry).
 */
function buildManifestCommand(
  variant: unknown,
  descriptors: readonly CommandDescriptor[],
): ManifestCommand | null {
  const action = readVariantAction(variant);
  if (action === null) return null;

  const targetCommandId = ACTION_TARGET_COMMAND[action];
  if (targetCommandId === undefined) return null;

  // Only AI-callable targets are advertised (the same fail-closed flag the
  // safe contour enforces). A flipped flag drops the command here automatically.
  if (!isAiCallableCommand(targetCommandId, descriptors)) return null;

  const descriptor = getDescriptorById(targetCommandId, descriptors);
  if (descriptor === null) return null;

  // Refines JSON Schema cannot express (cross-field distance, finiteness) ride
  // on `preconditions`; omit the field entirely when the action has none.
  const preconditions = actionPreconditions(action);

  return {
    name: action,
    description: descriptor.help.en.short,
    summaryRu: descriptor.help.ru.short,
    inputSchema: buildActionInputSchema(action, variant),
    targetCommandId,
    domain: descriptor.category,
    aliases: { en: descriptor.aliases.en, ru: descriptor.aliases.ru },
    examples: descriptor.help.en.examples,
    ...(preconditions !== undefined ? { preconditions } : {}),
  };
}

/**
 * Build the AI-callable command list, keyed by `action`, sorted by `name`.
 * Source: `aiCommandSchema` union variants ∩ `isAiCallableCommand`. Exported so
 * the serving layer + the anti-drift test share the one derivation.
 */
export function buildManifestCommands(
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): ManifestCommand[] {
  const commands: ManifestCommand[] = [];
  for (const variant of aiCommandSchema.options) {
    const command = buildManifestCommand(variant, descriptors);
    if (command !== null) commands.push(command);
  }
  commands.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return commands;
}

/**
 * Deterministic, byte-stable hash over the manifest's guard-relevant inputs
 * (the sorted action list + each command's JSON Schema + its precondition
 * notes). Changes iff a guard-relevant input changes; byte-stable otherwise. The
 * agent echoes it as `manifestVersion`; a mismatch vs the live build → `409
 * manifest-stale`. Preconditions are guard-relevant (they describe runtime
 * rejection rules the inputSchema cannot encode), so they participate in the
 * hash — editing a precondition note busts the version. FNV-1a 32-bit over a
 * canonical JSON projection — no crypto dependency, no Node-only API (runs in
 * the browser build too).
 */
export function computeManifestSchemaVersion(
  commands: readonly ManifestCommand[],
): string {
  const canonical = JSON.stringify(
    commands.map((c) => [
      c.name,
      c.targetCommandId,
      c.inputSchema,
      c.preconditions ?? null,
    ]),
  );
  let hash = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i += 1) {
    hash ^= canonical.charCodeAt(i);
    // FNV-1a 32-bit prime multiply, kept in uint32 via Math.imul.
    hash = Math.imul(hash, 0x01000193);
  }
  return `sha-fnv1a32:${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

/**
 * Build the FULL capability manifest CONTENT (commands + reject taxonomy +
 * protocol primer + provenance). The serving layer stamps `source` /
 * `authoritative` (a running browser → `live`/`true`; the bundled companion
 * snapshot → `static`/`false`).
 *
 * Pure + deterministic for a given descriptor array — no I/O, no AiCommand
 * parse, no EditorCommand, no `applyCommand` (INV-1: describing the surface is
 * never dispatching on it).
 */
export function buildCapabilityManifest(
  options: {
    readonly source?: "live" | "static";
    readonly descriptors?: readonly CommandDescriptor[];
  } = {},
): CapabilityManifest {
  const descriptors = options.descriptors ?? ALL_COMMAND_DESCRIPTORS;
  const source = options.source ?? "static";
  const commands = buildManifestCommands(descriptors);
  return {
    ok: true,
    source,
    authoritative: source === "live",
    protocolVersion: AGENT_BRIDGE_PROTOCOL_VERSION,
    schemaVersion: computeManifestSchemaVersion(commands),
    generatorVersion: AGENT_BRIDGE_MANIFEST_GENERATOR_VERSION,
    descriptorSchemaVersion: AGENT_BRIDGE_DESCRIPTOR_SCHEMA_VERSION,
    commands,
    // Static caps are advisory ([N5]): `limits.authoritative` mirrors the
    // manifest source (the static snapshot is non-authoritative; only the live
    // build-accurate manifest is). The running companion's `413`/`429` win.
    limits: { ...AGENT_BRIDGE_STATIC_LIMITS, authoritative: source === "live" },
    rejectTaxonomy: AGENT_BRIDGE_REJECT_TAXONOMY,
    protocolPrimer: AGENT_BRIDGE_PROTOCOL_PRIMER,
  };
}
